import i18n from "i18next";

// for presentation

export const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
};

export const formatMessageTimestamp = (timestamp: string): string => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    
    const isToday = messageDate.toDateString() === now.toDateString();
    const isThisYear = messageDate.getFullYear() === now.getFullYear();
    
    const hours = messageDate.getHours().toString().padStart(2, '0');
    const minutes = messageDate.getMinutes().toString().padStart(2, '0');
    
    if (isToday) {
        return `${hours}:${minutes}`;
    }
    
    const day = messageDate.getDate().toString().padStart(2, '0');
    const month = (messageDate.getMonth() + 1).toString().padStart(2, '0');
    
    if (isThisYear) {
        return `${day}.${month} ${hours}:${minutes}`;
    }
    
    const year = messageDate.getFullYear();
    return `${day}.${month}.${year} ${hours}:${minutes}`;
};

export const isISODateString = (value: any) => {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
};

export const formatDateWithLanguage = (date: string | Date, specifiedLocale?: string): string => {
    const locale = specifiedLocale ?? i18n.language;

    const normalized = typeof date === 'string'
        ? date.replace(/(\.\d{3})\d+/, '$1')
        : date;

    const parsed = new Date(normalized);

    if (isNaN(parsed.getTime())) return '';

    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
};

export const formatDateWithLanguageToOnlyDate = (date: string | Date, specifiedLocale?: string): string => {
    const locale = specifiedLocale ?? i18n.language;

    const normalized = typeof date === 'string'
        ? date.replace(/(\.\d{3})\d+/, '$1')
        : date;

    const parsed = new Date(normalized);

    if (isNaN(parsed.getTime())) return '';

    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(parsed);
};

export const formatDateWithLanguageToLongDate = (date: string | Date, specifiedLocale?: string): string => {
    const locale = specifiedLocale ?? i18n.language;

    const normalized = typeof date === 'string'
        ? date.replace(/(\.\d{3})\d+/, '$1')
        : date;

    const parsed = new Date(normalized);

    if (isNaN(parsed.getTime())) return '';

    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsed);
};

// for math counting and validation

export const formatDateToCount = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // YYYY-MM-DD
};

export const parseDateToCount = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

// to count remainig time in days and percent

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type LicenseTime = {
    remainingTimePercentage: number;
    remainingDaysNumber: number;
}

export const getLicenseTime = (
    activationDate: string | Date,
    expirationDate: string | Date,
    now: Date = new Date(),
): LicenseTime => {
    const start = new Date(activationDate).getTime();
    const end = new Date(expirationDate).getTime();
    const current = now.getTime();

    const total = end - start;

    if (Number.isNaN(total) || total <= 0) {
        return { remainingTimePercentage: 0, remainingDaysNumber: 0 };
    }

    const remainingMs = Math.min(Math.max(end - current, 0), total);

    return {
        remainingTimePercentage: Math.floor((remainingMs / total) * 100),
        remainingDaysNumber: Math.ceil(remainingMs / MS_PER_DAY),
    }
};